import axios from 'axios';
import { API } from '@/config/api';

const state = {
    summaries: [],
    currentSummary: null,
    loading: false,
    error: null,
};

const mutations = {
    SET_SUMMARIES(state, summaries) {
        state.summaries = summaries;
    },
    SET_CURRENT_SUMMARY(state, summary) {
        state.currentSummary = summary;
    },
    ADD_SUMMARY(state, summary) {
        state.summaries.unshift(summary);
    },
    UPDATE_SUMMARY(state, updatedSummary) {
        const index = state.summaries.findIndex(s => s.id === updatedSummary.id);
        if (index !== -1) {
            state.summaries.splice(index, 1, updatedSummary);
        }
    },
    DELETE_SUMMARY(state, summaryId) {
        state.summaries = state.summaries.filter(s => s.id !== summaryId);
    },
    SET_LOADING(state, loading) {
        state.loading = loading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
};

const actions = {
    /**
     * Get all summaries for a transcription
     */
    async getSummarizationOfTranscription({ commit }, { transcriptionId }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        try {
            const response = await axios.post(
                API.SUMMARY_GET,
                {
                    transcriptionId,
                    token: 'test'
                }
            );

            if (response.data.success) {
                const transcriptionData = response.data.result?.transcriptionWithSummarizations;
                const summaries = transcriptionData?.summarizations || [];
                commit('SET_SUMMARIES', summaries);
                return { success: true, data: summaries };
            } else {
                commit('SET_ERROR', 'Failed to fetch summaries');
                return { success: false, error: 'Failed to fetch summaries' };
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Error fetching summaries';
            commit('SET_ERROR', errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            commit('SET_LOADING', false);
        }
    },

    /**
     * Get a single summary by ID
     */
    async getOneSummarization({ commit }, { summarizationId }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        try {
            const response = await axios.post(
                `${process.env.VUE_APP_API_BASE_URL}/api/frontend/getOneSummarization`,
                {
                    summarizationId,
                    token: 'test'
                }
            );

            if (response.data.success) {
                const summary = response.data.result?.summarization;
                commit('SET_CURRENT_SUMMARY', summary);
                return { success: true, data: summary };
            } else {
                commit('SET_ERROR', 'Failed to fetch summary');
                return { success: false, error: 'Failed to fetch summary' };
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Error fetching summary';
            commit('SET_ERROR', errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            commit('SET_LOADING', false);
        }
    },

    /**
     * Create a new summary
     */
    async createSummary({ commit }, { text, language, extraPrompt, transcriptionId }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        try {
            const formData = new FormData();
            formData.append('text', text);
            formData.append('language', language);
            formData.append('extra_prompt', extraPrompt);
            formData.append('transcription_id', transcriptionId);

            const response = await axios.post(
                API.SUMMARY_CREATE,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            if (response.data.success) {
                const newSummary = response.data.result?.summarization;
                if (newSummary) {
                    commit('ADD_SUMMARY', newSummary);
                }
                return {
                    success: true,
                    data: newSummary,
                    message: response.data.message || 'Summary created successfully'
                };
            } else {
                commit('SET_ERROR', 'Failed to create summary');
                return { success: false, error: 'Failed to create summary' };
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Error creating summary';
            commit('SET_ERROR', errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            commit('SET_LOADING', false);
        }
    },

    /**
     * Update an existing summary
     */
    async updateSummarization({ commit }, { summarizationId, text }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        try {
            const response = await axios.post(
                `${process.env.VUE_APP_API_BASE_URL}/api/frontend/updateSummarization`,
                {
                    summarizationId,
                    text,
                    token: 'test'
                }
            );

            if (response.data.success) {
                const updatedSummary = response.data.result?.summarization;
                if (updatedSummary) {
                    commit('UPDATE_SUMMARY', updatedSummary);
                }
                return {
                    success: true,
                    data: updatedSummary,
                    message: 'Summary updated successfully'
                };
            } else {
                commit('SET_ERROR', 'Failed to update summary');
                return { success: false, error: 'Failed to update summary' };
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Error updating summary';
            commit('SET_ERROR', errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            commit('SET_LOADING', false);
        }
    },

    /**
     * Delete a summary
     */
    async deleteSummarization({ commit }, { summarizationId }) {
        commit('SET_LOADING', true);
        commit('SET_ERROR', null);

        try {
            const response = await axios.post(
                `${process.env.VUE_APP_API_BASE_URL}/api/frontend/deleteSummarization`,
                {
                    summarizationId,
                    token: 'test'
                }
            );

            if (response.data.success) {
                commit('DELETE_SUMMARY', summarizationId);
                return { success: true, message: 'Summary deleted successfully' };
            } else {
                commit('SET_ERROR', 'Failed to delete summary');
                return { success: false, error: 'Failed to delete summary' };
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Error deleting summary';
            commit('SET_ERROR', errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            commit('SET_LOADING', false);
        }
    },

    /**
     * Clear summaries
     */
    clearSummaries({ commit }) {
        commit('SET_SUMMARIES', []);
        commit('SET_CURRENT_SUMMARY', null);
    },
};

const getters = {
    allSummaries: state => state.summaries,
    currentSummary: state => state.currentSummary,
    isLoading: state => state.loading,
    error: state => state.error,
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
